import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@material-ui/core';
import { AlertBanner, Loader } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { setDiscoveryCompleted } from '../../redux/migrationSlice';
import { getSourceConfig } from '../../utils/migrationUtils';
import { formatApiError } from '../../utils/apiUtils';
import {
  fetchSourceStatus,
  triggerConnectedDiscovery,
  uploadDisconnectedDiscoveryFile,
} from '../../redux/migrationThunks';
import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  BUTTON_LABELS,
  TITLES,
  DESCRIPTIONS,
} from '../../constants/stringConstants';
import MigrationStep from './MigrationStep';
import PrerequisitesModal from './PrerequisitesModal';
import DisconnectedDiscoveryModal from './DisconnectedDiscoveryModal';
import useStyles from './styles/index.styles';

function GettingStarted({
  sourceType,
  sourceID,
  onNavigate,
  onConnectionSaved,
  dataMigrationTabEnabled,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [showPrerequisites, setShowPrerequisites] = useState(false);
  const [showDisconnectedDiscovery, setShowDisconnectedDiscovery] = useState(false);
  const [error, setError] = useState(null);
  const [fileUploadLoading, setFileUploadLoading] = useState(false);
  const { sourceStatus } = useSelector((state) => state.migration);
  const discoveryCompleted =
    !!sourceStatus?.discoveryCompleted &&
    sourceStatus?.discoveryStatus !== 'IN_PROGRESS' &&
    sourceStatus?.discoveryStatus !== 'FAILED';
  // TODO: Remove hardcoded true once API is ready
  const disconnectedDiscovery = !!sourceStatus?.disconnectedDiscovery;

  const config = getSourceConfig(sourceType);
  const pollingRef = useRef(null);
  const [statusBanner, setStatusBanner] = useState({ visible: false, type: 'Info', message: '' });

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startStatusPolling = useCallback(() => {
    stopPolling();
    setStatusBanner({ visible: true, type: 'Info', message: 'Discovery in progress...' });

    const INITIAL_DELAY = 5000;
    const MAX_DELAY = 60000;
    let attempt = 0;

    const poll = async () => {
      if (!sourceID) return;
      try {
        const result = await dispatch(fetchSourceStatus(sourceID)).unwrap();
        const status = result?.data;
        if (status?.response) {
          const bannerType =
            status.discoveryStatus === 'FAILED'
              ? 'Critical'
              : status.discoveryStatus === 'COMPLETED'
                ? 'Success'
                : 'Info';
          setStatusBanner({ visible: true, type: bannerType, message: status.response });
        }
        if (status?.discoveryStatus === 'COMPLETED') {
          stopPolling();
          setFileUploadLoading(false);
          setStatusBanner({
            visible: true,
            type: 'Success',
            message: status.response || 'Discovery completed successfully',
          });
          dispatch(setDiscoveryCompleted());
          setTimeout(() => onNavigate(1), 3000);
        } else if (status?.discoveryStatus === 'FAILED') {
          stopPolling();
          setFileUploadLoading(false);
          setStatusBanner({
            visible: true,
            type: 'Critical',
            message: status.response || 'Discovery failed',
          });
        } else {
          attempt += 1;
          const delay = Math.min(INITIAL_DELAY * 2 ** attempt, MAX_DELAY);
          pollingRef.current = setTimeout(poll, delay);
        }
      } catch (err) {
        stopPolling();
        setFileUploadLoading(false);
      }
    };

    pollingRef.current = setTimeout(poll, INITIAL_DELAY);
  }, [sourceID, dispatch, stopPolling, onNavigate]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const handleDiscover = useCallback(async () => {
    if (discoveryCompleted) {
      setError(ERROR_MESSAGES.DISCOVERY_ALREADY_COMPLETED);
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (!sourceID) {
      setError(ERROR_MESSAGES.SOURCE_NOT_CONFIGURED_DETAIL);
      return;
    }

    setFileUploadLoading(true);
    setError(null);

    try {
      await dispatch(triggerConnectedDiscovery({ sourceId: sourceID })).unwrap();

      startStatusPolling();
    } catch (err) {
      setFileUploadLoading(false);
      setError(formatApiError(err));
    }
  }, [sourceID, dispatch, discoveryCompleted, startStatusPolling]);

  const handleDiscoverClick = useCallback(() => {
    if (disconnectedDiscovery) {
      setShowDisconnectedDiscovery(true);
    } else {
      handleDiscover();
    }
  }, [disconnectedDiscovery, handleDiscover]);

  const handleDisconnectedDiscoverySubmit = useCallback(
    async (files) => {
      setShowDisconnectedDiscovery(false);
      setFileUploadLoading(true);
      setError(null);

      const file = files[0]?.file || files[0];
      try {
        await dispatch(uploadDisconnectedDiscoveryFile({ sourceId: sourceID, file })).unwrap();

        startStatusPolling();
      } catch (err) {
        setFileUploadLoading(false);
        setError(err?.message || ERROR_MESSAGES.FAILED_UPLOAD_FILE);
      }
    },
    [dispatch, sourceID, startStatusPolling]
  );

  if (sourceID === undefined) {
    return (
      <div className={classes.loader}>
        <Loader kind="loop" size="large" format="inline" color="brand" />
      </div>
    );
  }

  const migrationSteps = [
    {
      id: 'prerequisites',
      number: '1',
      title: TITLES.PREREQUISITES,
      description: DESCRIPTIONS.PREREQUISITES,
      action: () => setShowPrerequisites(true),
      buttonText: BUTTON_LABELS.SETTINGS,
      disabled: false,
      completed: false,
      showButton: true,
    },
    {
      id: 'discover-objects',
      number: '2',
      title: TITLES.DISCOVER_OBJECTS,
      description: `Connect to your ${config.name} environment and get visibility to different objects present. Once discovered you can migrate the objects to Saviynt.`,
      action: handleDiscoverClick,
      buttonText: (() => {
        if (fileUploadLoading) return BUTTON_LABELS.DISCOVERING;
        if (discoveryCompleted) return SUCCESS_MESSAGES.COMPLETED_CHECKMARK;
        return BUTTON_LABELS.DISCOVER;
      })(),
      disabled: discoveryCompleted || !sourceID || fileUploadLoading,
      completed: discoveryCompleted,
      showButton: !discoveryCompleted,
    },
    {
      id: 'migrate-configuration',
      number: '3',
      title: TITLES.CONFIGURATION_MIGRATION,
      description: `Choose and migrate various configuration objects & applications from ${config.name}.`,
      action: () => {
        if (!discoveryCompleted) {
          setError(ERROR_MESSAGES.COMPLETE_DISCOVERY_FIRST_CONFIG);
          setTimeout(() => setError(null), 3000);
          return;
        }
        onNavigate(2);
      },
      buttonText: BUTTON_LABELS.MIGRATE_CONFIGURATION,
      disabled: !discoveryCompleted,
      completed: false,
      showButton: true,
    },
    {
      id: 'migrate-data',
      number: '4',
      title: TITLES.DATA_MIGRATION,
      description: `Build Identity Warehouse in Saviynt by migrating one time import of different data objects from ${config.name} to Saviynt.`,
      action: () => {
        if (!discoveryCompleted) {
          setError(ERROR_MESSAGES.COMPLETE_DISCOVERY_FIRST_DATA);
          setTimeout(() => setError(null), 3000);
          return;
        }
        onNavigate(3);
      },
      buttonText: BUTTON_LABELS.MIGRATE_DATA,
      disabled: !dataMigrationTabEnabled,
      completed: false,
      showButton: true,
    },
  ];

  return (
    <div className={classes.container}>
      {fileUploadLoading &&
        ReactDOM.createPortal(
          <div className={classes.fullPageLoader}>
            <Loader kind="loop" size="large" format="inline" color="inverse" />
            {statusBanner.message && (
              <Typography className={classes.loaderStatusText}>{statusBanner.message}</Typography>
            )}
          </div>,
          document.body
        )}

      {error && (
        <div className={classes.errorBox}>
          <Typography variant="body2" className={classes.errorText}>
            {error}
          </Typography>
        </div>
      )}

      {statusBanner.visible &&
        !fileUploadLoading &&
        ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              top: '16px',
              right: '16px',
              zIndex: 10000,
              width: 'fit-content',
              marginLeft: 'auto',
            }}>
            <AlertBanner
              colorTheme={statusBanner.type}
              title={statusBanner.type === 'Success' ? 'Success' : 'Error'}
              description={statusBanner.message}
              isVisible={statusBanner.visible}
              onCancel={() => setStatusBanner((prev) => ({ ...prev, visible: false }))}
              shouldAutoDismiss
              autoDismissDelay={5000}
              isCloseButtonVisible
            />
          </div>,
          document.body
        )}

      <PrerequisitesModal
        isOpen={showPrerequisites}
        onClose={() => setShowPrerequisites(false)}
        sourceID={sourceID}
        sourceType={sourceType}
        onConnectionSaved={(connectionName) => {
          setError(null);
          if (onConnectionSaved) onConnectionSaved(connectionName);
        }}
      />

      <DisconnectedDiscoveryModal
        isOpen={showDisconnectedDiscovery}
        onClose={() => setShowDisconnectedDiscovery(false)}
        onSubmit={handleDisconnectedDiscoverySubmit}
      />

      <div className={classes.stepsContainer}>
        {migrationSteps.map((step, index) => (
          <MigrationStep
            key={step.id}
            step={step}
            index={index}
            totalSteps={migrationSteps.length}
          />
        ))}
      </div>
    </div>
  );
}

GettingStarted.propTypes = {
  sourceType: PropTypes.string.isRequired,
  sourceID: PropTypes.string,
  onNavigate: PropTypes.func.isRequired,
  onConnectionSaved: PropTypes.func,
};

GettingStarted.defaultProps = {
  sourceID: undefined,
  onConnectionSaved: null,
};

export default GettingStarted;
